import request from '../../utils/request';
import { Result } from '@/models/general';
import {
  WorkPointRuleView,
  WorkPointEstimateView,
  WorkPointBreakdownView,
  CompensationReportView,
  PayrollEntriesResultView,
  CreateWorkPointRuleInput,
  UpdateWorkPointRuleInput,
  WorkPointEstimateQuery,
  CompensationReportQuery,
  MatchFormatDurationsView,
  UpdateMatchFormatDurationsInput,
} from '@/models/tournament';

export async function getCalculationRules() {
  const response = await request.get<Result<WorkPointRuleView[]>>('WorkPoint/GetCalculationRules');
  return response.data;
}

export async function getActiveRule() {
  const response = await request.get<Result<WorkPointRuleView>>('WorkPoint/GetActiveRule');
  return response.data;
}

export async function createRule(input: CreateWorkPointRuleInput) {
  const response = await request.post<Result<WorkPointRuleView>>('WorkPoint/CreateRule', input);
  return response.data;
}

export async function updateRule(input: UpdateWorkPointRuleInput) {
  const response = await request.put<Result<WorkPointRuleView>>('WorkPoint/UpdateRule', input);
  return response.data;
}

/** Company-wide match duration per format (HR/Admin). */
export async function getMatchDurations() {
  const response = await request.get<Result<MatchFormatDurationsView>>('WorkPoint/GetMatchDurations');
  return response.data;
}

/** Saves the company-wide match durations. Only matches created or edited afterwards pick them up. */
export async function updateMatchDurations(input: UpdateMatchFormatDurationsInput) {
  const response = await request.put<Result<MatchFormatDurationsView>>('WorkPoint/UpdateMatchDurations', input);
  return response.data;
}

export async function estimatePoints(query: WorkPointEstimateQuery) {
  const response = await request.get<Result<WorkPointEstimateView>>('WorkPoint/EstimatePoints', { params: query });
  return response.data;
}

export async function getMatchBreakdown(matchId: number) {
  const response = await request.get<Result<WorkPointBreakdownView[]>>(`WorkPoint/GetMatchBreakdown/${matchId}`);
  return response.data;
}

export async function getCompensationReport(query: CompensationReportQuery) {
  const response = await request.get<Result<CompensationReportView>>('WorkPoint/GetCompensationReport', { params: query });
  return response.data;
}

/** Server clamps `PageSize` to 1..100 (`WorkPointCalculationService.GetCompensationReportAsync`). */
const COMPENSATION_PAGE_SIZE = 100;
/** Runaway guard: 100 pages x 100 referees is far past any real tenant. */
const COMPENSATION_MAX_PAGES = 100;

/**
 * The compensation report is server-paged (default 25) while `totalWorkPoints` / `totalCurrencyAmount`
 * are computed over ALL referees. Reading a single page therefore produced a footer, a "Total Referees"
 * count and a CSV that silently covered 25 referees while the totals covered everyone. Walk every page
 * so line-level output and the server totals describe the same set.
 */
export async function getCompensationReportAllPages(query: CompensationReportQuery) {
  const first = await getCompensationReport({ ...query, page: 1, pageSize: COMPENSATION_PAGE_SIZE });
  if (first.errors.length > 0 || !first.payload) return first;

  const lines = [...first.payload.lines];
  const totalPages = Math.min(first.payload.totalPages ?? 1, COMPENSATION_MAX_PAGES);

  for (let page = 2; page <= totalPages; page++) {
    const next = await getCompensationReport({ ...query, page, pageSize: COMPENSATION_PAGE_SIZE });
    if (next.errors.length > 0 || !next.payload) return next; // surface the failure, never a half report
    if (next.payload.lines.length === 0) break;
    lines.push(...next.payload.lines);
  }

  return {
    ...first,
    payload: { ...first.payload, lines, page: 1, pageSize: lines.length },
  };
}

export async function recalculateMatch(matchId: number) {
  const response = await request.post<Result<WorkPointBreakdownView[]>>(`WorkPoint/RecalculateMatch/${matchId}`);
  return response.data;
}

export async function generatePayrollEntries(input: { startDate: string; endDate: string; tournamentId?: number; refereeUserId?: string }) {
  const response = await request.post<Result<PayrollEntriesResultView>>('WorkPoint/GeneratePayrollEntries', input);
  return response.data;
}
